/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */
import { __ } from '@oat-sa-private/ui-core';

/**
 * Factory returning a 'validate' function pre-loaded with the necessary props
 * @param {Object} props
 * @param {Boolean} [props.required]
 * @param {Integer} [props.maxlength]
 * @param {Integer} [props.minlength]
 * @param {String} [props.pattern]
 * @param {String} [props.patternMessage]
 * @returns {Function}
 */
export default function validationFactory({ required, maxlength, minlength, pattern, patternMessage } = {}) {
    const messages = {
        required: __('This is a required field.'),
        maxlength: __('The maximum length is %d.', maxlength),
        minlength: __('The minimum length is %d.', minlength),
        pattern: patternMessage || __('The format must match the pattern.'),
        default: __('Invalid value.')
    };

    /**
     * @typedef {Object} ValidityMessage
     * @property {Boolean} valid
     * @property {String} msg
     * @property {String} [type] - this may be used as key to control feedback display (so do not change it)
     */
    /**
     * @typedef {Object} Validity
     * @property {ValidityMessage[]} messages
     * @property {Boolean} overallValidity
     */
    /**
     * Validation callback
     * Checks value against required, type, pattern, minlength & maxlength props (if present)
     * Uses HTMLInput.validity API
     * @param {HTMLElement} inputElement
     * @param {Object} customValidity - a validity result from another source than the element itself
     * @param {String} [customValidity.msg]
     * @param {Boolean} [customValidity.valid]
     * @returns {Validity} validation result
     */
    return function validate(inputElement, customValidity) {
        if (!(inputElement instanceof HTMLInputElement || inputElement instanceof HTMLTextAreaElement)) {
            throw new TypeError('Can only validate Inputs and TextAreas');
        }

        const feedbackMessages = [];

        if (required) {
            feedbackMessages.push({
                msg: messages.required,
                valid: !inputElement.validity.valueMissing,
                type: 'required'
            });
        }
        if (pattern) {
            feedbackMessages.push({
                msg: messages.pattern,
                valid: !inputElement.validity.patternMismatch,
                type: 'pattern'
            });
        }
        if (maxlength) {
            //if value was updated programmatically, validity.tooLong may not be recalculated
            feedbackMessages.push({
                msg: messages.maxlength,
                valid: !inputElement.value.length || inputElement.value.length <= maxlength,
                type: 'maxlength'
            });
        }
        if (minlength) {
            //if value was updated programmatically, validity.tooShort may not be recalculated
            feedbackMessages.push({
                msg: messages.minlength,
                valid: !inputElement.value.length || inputElement.value.length >= minlength,
                type: 'minlength'
            });
        }

        const invalidCount = feedbackMessages.filter(message => !message.valid).length;

        // Only apply customValidity if defined and no other invalid rules
        if (customValidity && !customValidity.valid && !invalidCount) {
            feedbackMessages.push({
                msg: customValidity.msg || messages.default,
                valid: customValidity.valid,
                type: 'custom'
            });
        }

        if (invalidCount) {
            inputElement.setCustomValidity(feedbackMessages[feedbackMessages.length - 1].msg);
        } else {
            inputElement.setCustomValidity('');
        }

        return {
            messages: feedbackMessages,
            overallValidity: feedbackMessages.every(message => message.valid)
        };
    };
}
